import { Fragment, useMemo, useState } from 'react';
import clsx from 'clsx';
import type { HeatmapCell, HeatmapRow } from '@/domain/queueEngine';
import type { Studio } from '@/domain/types';
import {
  availabilityCellState,
  intensityClass,
  DAY_OFF_MARKER,
} from '@/components/queue/availabilityCell';

// Mirrors intensityClass: 5h+ free on a working day is the "blood"/urgent tier.
const URGENT_FREE_HOURS = 5;

/** A cell is a fillable gap only on a working (non-day-off) day with free hours. */
function cellIsGap(row: HeatmapRow, cell: HeatmapCell): boolean {
  const st = availabilityCellState(row.artist.availability, cell);
  return st.kind === 'working' && st.freeHours > 0;
}

/** A row needs action if any working day still has free hours to fill. */
function rowHasGap(row: HeatmapRow): boolean {
  return row.cells.some((c) => cellIsGap(row, c));
}

/** A row is urgent if any working day has a wide-open (5h+) gap. */
function rowHasUrgentGap(row: HeatmapRow): boolean {
  return row.cells.some((c) => cellIsGap(row, c) && c.freeHours >= URGENT_FREE_HOURS);
}

type FilterMode = 'all' | 'gaps' | 'urgent';

const FILTER_MODES: Array<{ key: FilterMode; label: string }> = [
  { key: 'all', label: 'All' },
  { key: 'gaps', label: 'Gaps' },
  { key: 'urgent', label: 'Urgent' },
];

interface Props {
  rows: HeatmapRow[];
  studios: Record<string, Studio>;
  selected?: { artistId: string; dayIso: string } | null;
  onSelect: (cell: HeatmapCell) => void;
  onClear: () => void;
}

const DAY_LABELS: Record<HeatmapCell['dayOfWeek'], string> = {
  sun: 'S',
  mon: 'M',
  tue: 'T',
  wed: 'W',
  thu: 'T',
  fri: 'F',
  sat: 'S',
};

export default function ArtistAvailabilityHeatmap({
  rows,
  studios,
  selected,
  onSelect,
  onClear,
}: Props) {
  const dayHeaders = useMemo(() => {
    const first = rows[0]?.cells ?? [];
    return first.map((c) => {
      const d = new Date(c.dayIso);
      return {
        label: DAY_LABELS[c.dayOfWeek],
        date: d.getDate(),
        dayIso: c.dayIso,
      };
    });
  }, [rows]);

  const [filterMode, setFilterMode] = useState<FilterMode>('all');

  const groupedRows = useMemo(() => {
    const visible =
      filterMode === 'urgent'
        ? rows.filter(rowHasUrgentGap)
        : filterMode === 'gaps'
          ? rows.filter(rowHasGap)
          : rows;
    const map = new Map<string, HeatmapRow[]>();
    for (const r of visible) {
      const sid = r.artist.studioId;
      if (!map.has(sid)) map.set(sid, []);
      map.get(sid)!.push(r);
    }
    return [...map.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([studioId, group]) => ({
        studioId,
        studioName: studios[studioId]?.name ?? studioId.replace('-', ' '),
        rows: group,
      }));
  }, [rows, studios, filterMode]);
  const showStudioHeaders = groupedRows.length > 1;
  const modeCounts = useMemo(
    () => ({
      all: rows.length,
      gaps: rows.filter(rowHasGap).length,
      urgent: rows.filter(rowHasUrgentGap).length,
    }),
    [rows],
  );

  return (
    <section
      data-testid="artist-heatmap"
      className="rounded-lg border border-gold-dim/20 bg-ink-deep p-4"
    >
      <header className="flex items-center justify-between mb-3">
        <div>
          <p className="label-small-caps text-gold-bright">Artist availability</p>
          <p className="font-inter text-[10px] text-cream-faint uppercase tracking-widest mt-1">
            Next {dayHeaders.length} days · number = free hours · C/L = comp / leave · click to fill
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div
            className="inline-flex items-center rounded-md border border-gold-dim/30 overflow-hidden"
            data-testid="heatmap-filter"
            title="All artists · only those with gaps · only urgent (5h+) gaps"
          >
            {FILTER_MODES.map((m) => {
              const active = filterMode === m.key;
              const urgent = m.key === 'urgent';
              return (
                <button
                  key={m.key}
                  type="button"
                  onClick={() => setFilterMode(m.key)}
                  className={clsx(
                    'px-2 py-1 text-[10px] font-inter uppercase tracking-widest transition',
                    active && urgent && 'bg-blood/15 text-blood',
                    active && !urgent && 'bg-gold/15 text-gold-bright',
                    !active && 'text-cream-soft hover:text-gold-bright',
                  )}
                  data-testid={`heatmap-filter-${m.key}`}
                >
                  {m.label}{' '}
                  <span className="text-cream-faint">{modeCounts[m.key]}</span>
                </button>
              );
            })}
          </div>
          {selected ? (
            <button
              type="button"
              onClick={onClear}
              className="btn-quiet text-[10px]"
              data-testid="heatmap-clear"
            >
              Clear filter
            </button>
          ) : null}
        </div>
      </header>

      <div className="overflow-x-auto">
        <table className="w-full text-[10px] font-mono">
          <thead>
            <tr>
              <th className="text-left text-cream-faint font-normal pr-2 py-1 whitespace-nowrap">
                Artist
              </th>
              {dayHeaders.map((h) => (
                <th
                  key={h.dayIso}
                  className="text-cream-faint font-normal px-0.5 py-1 text-center"
                >
                  <div className="leading-none">{h.label}</div>
                  <div className="leading-none mt-0.5 text-gold-dim">{h.date}</div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {groupedRows.map((group) => (
              <Fragment key={`studio-group-${group.studioId}`}>
                {showStudioHeaders ? (
                  <tr data-testid={`heatmap-studio-${group.studioId}`}>
                    <td
                      colSpan={dayHeaders.length + 1}
                      className="pt-3 pb-1 label-small-caps text-gold-bright"
                    >
                      {group.studioName}
                    </td>
                  </tr>
                ) : null}
                {group.rows.map((row) => (
                  <tr key={row.artist.id}>
                    <td className="pr-2 py-0.5 text-cream-soft">
                      <span className="truncate max-w-[110px] inline-block align-middle">
                        {row.artist.displayName}
                      </span>
                    </td>
                    {row.cells.map((cell) => {
                      const testId = `heatmap-cell-${row.artist.id}-${cell.dayIso.slice(0, 10)}`;
                      const st = availabilityCellState(row.artist.availability, cell);
                      // Comp days + leave get a distinct marker; routine RDOs render
                      // as a quiet "off" cell so they don't clutter the grid.
                      if (st.kind === 'comp' || st.kind === 'leave') {
                        const m = DAY_OFF_MARKER[st.kind];
                        return (
                          <td key={cell.dayIso} className="px-0.5 py-0.5">
                            <div
                              data-testid={testId}
                              title={`${row.artist.displayName} · ${m.label}${st.note ? ` — ${st.note}` : ''}`}
                              className={clsx(
                                'w-6 h-6 rounded-sm flex items-center justify-center border border-transparent',
                                m.cls,
                              )}
                            >
                              {m.letter}
                            </div>
                          </td>
                        );
                      }
                      const isSelected =
                        selected?.artistId === cell.artistId &&
                        selected?.dayIso === cell.dayIso;
                      return (
                        <td key={cell.dayIso} className="px-0.5 py-0.5">
                          <button
                            type="button"
                            onClick={() => onSelect(cell)}
                            disabled={st.off}
                            data-testid={testId}
                            title={
                              st.off
                                ? `${row.artist.displayName} off`
                                : `${row.artist.displayName} · ${st.freeHours}/${st.workingHours}h free`
                            }
                            className={clsx(
                              'w-6 h-6 rounded-sm flex items-center justify-center transition border',
                              st.off
                                ? 'bg-ink-black/60 text-cream-faint/30'
                                : intensityClass(st.freeHours, st.workingHours),
                              isSelected
                                ? 'border-gold-bright shadow-gold-glow'
                                : 'border-transparent hover:border-gold-bright/60',
                              st.off ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
                            )}
                          >
                            {st.off ? '·' : st.freeHours}
                          </button>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {groupedRows.length === 0 ? (
        <p
          className="font-serif text-cream-soft text-center py-4"
          data-testid="heatmap-all-filled"
        >
          {filterMode === 'urgent'
            ? 'No urgent gaps — nothing 5h+ open right now.'
            : 'Every artist is booked solid — no gaps to fill right now.'}
        </p>
      ) : null}

      <footer className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-3 text-[10px] font-inter uppercase tracking-widest text-cream-faint">
        <span className="flex items-center gap-1">
          <span className="w-3 h-3 rounded-sm bg-mint/55" /> Booked solid
        </span>
        <span className="flex items-center gap-1">
          <span className="w-3 h-3 rounded-sm bg-gold/55" /> 1–4h gap
        </span>
        <span className="flex items-center gap-1">
          <span className="w-3 h-3 rounded-sm bg-blood/55" /> 5h+ gap · urgent
        </span>
        <span className="text-cream-faint/60">·</span>
        <span className="flex items-center gap-1">
          <span className="w-3 h-3 rounded-sm bg-azure/45 flex items-center justify-center text-ink-black text-[8px]">C</span> Comp day
        </span>
        <span className="flex items-center gap-1">
          <span className="w-3 h-3 rounded-sm bg-gold-dim/55 flex items-center justify-center text-ink-black text-[8px]">L</span> Leave
        </span>
      </footer>
    </section>
  );
}
